import { Alert, Button, Slide, Snackbar, SnackbarOrigin } from '@mui/material';
import { createContext, useCallback, useContext, useState, type SyntheticEvent } from 'react';

import { snackBarDisplayTime } from '../../constants';

interface SnackBarData {
  message: string;
  color: SnackBarColor;
  anchorOrigin?: SnackbarOrigin;
  action?: { label: string; onClick: () => void };
}

interface SnackBarState extends SnackBarData {
  open: boolean;
}

type SnackBarColor = 'error' | 'warning' | 'info' | 'success';

export type SnackBarContextState = {
  addToast: (value: SnackBarData) => void;
};

interface ProviderProps {
  children: JSX.Element;
}

export const SnackBarContext = createContext<SnackBarContextState>({} as SnackBarContextState);

export const SnackBarContextProvider = ({ children }: ProviderProps) => {
  const [data, setData] = useState<SnackBarState | null>(null);

  const addToast = useCallback((snackData: SnackBarData) => setData({ ...snackData, open: true }), [setData]);

  const handleClose = (_event?: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setData((prev) => (prev ? { ...prev, open: false } : prev));
  };

  const handleExited = () => {
    setData(null);
  };

  return (
    <SnackBarContext.Provider value={{ addToast }}>
      {children}
      {data && (
        <Snackbar
          open={data.open}
          autoHideDuration={snackBarDisplayTime}
          onClose={handleClose}
          anchorOrigin={data.anchorOrigin || { vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'left', onExited: handleExited }}
          sx={{ pointerEvents: 'none' }}
        >
          <Alert
            severity={data.color}
            sx={{ pointerEvents: 'auto' }}
            action={data.action && (
              <Button color="inherit" size="small" onClick={() => {
                data.action?.onClick();
                handleClose();
              }}>
                {data.action.label}
              </Button>
            )}
          >
            {data.message}
          </Alert>
        </Snackbar>
      )}
    </SnackBarContext.Provider>
  );
};

export const useSnackBar = () => {
  const context = useContext(SnackBarContext);
  return { ...context };
};
